import { AdjustmentsHorizontalIcon } from '@heroicons/react/24/outline';
import type { SortingState } from '@tanstack/react-table';
import { useCallback, useMemo } from 'react';
import { EditColumns } from '../../components/edit-columns';
import { buildExceptionLogColumns } from '../../components/exception-log-table/columns';
import { MultiSelect, Select } from '../../components/forms';
import { LoadingOverlay } from '../../components/loading';
import { FilterWrapper } from '../../components/product-table/filters/filter-wrapper';
import { SearchInput } from '../../components/search-input';
import { ReportActions } from '../../components/tanstack-table/report-actions';
import { TanStackTable } from '../../components/tanstack-table/table';
import { useRouteRowSelection } from '../../components/tanstack-table/use-route-row-selection';
import { useTableRef } from '../../components/tanstack-table/use-table-ref';
import { PageToolbar } from '../../components/toolbar/page-toolbar';
import { ToolbarPopover } from '../../components/toolbar/popover';
import { SearchWithCount } from '../../components/toolbar/search-with-count';
import { ACTION_LABELS, EXCEPTION_ACTIONS, type ExceptionLog } from '../../data/exception-log';
import type { Product } from '../../data/product';
import {
  type StringOption,
  stringOptionsToValues,
  useStringOptions,
} from '../../helpers/string-options';
import {
  type ExceptionLogFilters,
  searchPlaceholders,
  useExceptionLogs,
  useFilteredExceptionLogs,
} from '../../hooks/exception-logs';
import { useEngagement } from '../../hooks/use-engagement';
import { useSearchFocus } from '../../hooks/use-search-focus';
import { useVisibleColumns } from '../../hooks/use-visible-columns';
import { exceptionLogsRoute, productReportsLayout, storeLayout } from '../../router';

const EXCEPTION_LOGS_COLUMNS_KEY = 'exception-logs';

const defaultFilters: ExceptionLogFilters = {
  period: 30,
};

const sorting: SortingState = [{ id: 'Timestamp', desc: true }];

const actionOptions: StringOption[] = EXCEPTION_ACTIONS.map((action) => ({
  value: action,
  label: ACTION_LABELS[action] ?? action,
}));

const periodOptions = [
  { value: '7', label: 'Last 7 days' },
  { value: '14', label: 'Last 14 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '60', label: 'Last 60 days' },
  { value: '90', label: 'Last 90 days' },
];

export function ExceptionLogsReport() {
  const { store } = storeLayout.useRouteContext();
  const timezone = store.timezone;
  const triggerEngagement = useEngagement('exception_logs_engaged', store.id);
  const { query: queryParam } = exceptionLogsRoute.useSearch();
  const { exceptionLogs, loading } = useExceptionLogs();
  const { filteredLogs, baselineCount, filters, setFilters, activeFilterCount, clearFilters } =
    useFilteredExceptionLogs(
      exceptionLogs,
      queryParam ? { ...defaultFilters, query: queryParam } : defaultFilters
    );
  const products = productReportsLayout.useLoaderData();

  const productLookup = useMemo(() => {
    const byId = new Map<string, Product>();
    for (const product of products) {
      byId.set(product.id, product);
    }

    return (log: ExceptionLog): Product | undefined => byId.get(log.code_num);
  }, [products]);

  const allColumns = useMemo(() => {
    return buildExceptionLogColumns(productLookup, timezone, store.id);
  }, [productLookup, timezone, store.id]);

  const { columns, columnVisibility, columnOrder } = useVisibleColumns(
    allColumns,
    EXCEPTION_LOGS_COLUMNS_KEY
  );

  const selectedActions = useStringOptions(filters.actions);
  const selectedInitials = useStringOptions(filters.initials);

  const initialsOptions: StringOption[] = useMemo(() => {
    const set = new Set<string>();
    for (const log of exceptionLogs) {
      if (log.initials.trim()) {
        set.add(log.initials);
      }
    }

    return [...set].sort().map((v) => ({ value: v, label: v }));
  }, [exceptionLogs]);

  const onActionsChange = useCallback(
    (actionOpts: readonly StringOption[]) => {
      triggerEngagement();
      const actions = stringOptionsToValues(actionOpts);
      setFilters((prev) => ({ ...prev, actions }));
    },
    [setFilters, triggerEngagement]
  );

  const onInitialsChange = useCallback(
    (opts: readonly StringOption[]) => {
      triggerEngagement();
      const initials = stringOptionsToValues(opts);
      setFilters((prev) => ({ ...prev, initials }));
    },
    [setFilters, triggerEngagement]
  );

  const onPeriodChange = useCallback(
    (value: string) => {
      triggerEngagement();
      setFilters((prev) => ({ ...prev, period: Number(value) }));
    },
    [setFilters, triggerEngagement]
  );

  const searchReference = useSearchFocus();
  const onSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      triggerEngagement();
      setFilters((prev) => ({ ...prev, query: e.target.value }));
    },
    [setFilters, triggerEngagement]
  );

  const [table, onTableReady] = useTableRef<ExceptionLog>();
  const { rowSelection, onRowSelectionChange, onRowClick: selectRow } = useRouteRowSelection();

  const onRowClick = useCallback(
    (...args: Parameters<typeof selectRow>) => {
      triggerEngagement();
      selectRow(...args);
    },
    [selectRow, triggerEngagement]
  );

  return (
    <>
      <SummaryStats logs={filteredLogs} filters={filters} setFilters={setFilters} />
      <PageToolbar
        left={
          <>
            <ToolbarPopover
              icon={<AdjustmentsHorizontalIcon className="size-5" />}
              label="Filters"
              badge={activeFilterCount}
              onClear={clearFilters}
            >
              <FilterWrapper label="Action">
                <MultiSelect
                  options={actionOptions}
                  value={selectedActions}
                  onChange={onActionsChange}
                  placeholder="Any"
                  isMulti
                />
              </FilterWrapper>
              <FilterWrapper label="Initials">
                <MultiSelect
                  options={initialsOptions}
                  value={selectedInitials}
                  onChange={onInitialsChange}
                  placeholder="Any"
                  isMulti
                />
              </FilterWrapper>
              <FilterWrapper label="Period">
                <Select<string>
                  value={String(filters.period ?? 30)}
                  onChange={onPeriodChange}
                  options={periodOptions}
                />
              </FilterWrapper>
            </ToolbarPopover>
            <EditColumns tableKey={EXCEPTION_LOGS_COLUMNS_KEY} sorting={sorting} />
          </>
        }
        right={
          <>
            <SearchWithCount quantity={filteredLogs.length} total={baselineCount} noun="exceptions">
              <SearchInput
                grouped
                value={filters.query ?? ''}
                onChange={onSearchChange}
                inputRef={searchReference}
                placeholders={searchPlaceholders}
                shortcut="meta+f"
              />
            </SearchWithCount>
            <ReportActions table={table} title="Exception Logs" />
          </>
        }
      />
      <TanStackTable
        data={filteredLogs}
        columns={columns}
        sorting={sorting}
        columnVisibility={columnVisibility}
        columnOrder={columnOrder}
        rowSelection={rowSelection}
        onRowSelectionChange={onRowSelectionChange}
        onRowClick={onRowClick}
        getRowId={(log, index) => `${log.timestamp.getTime()}-${index}`}
        onTableReady={onTableReady}
      />
      {loading && <LoadingOverlay />}
    </>
  );
}

function SummaryStats({
  logs,
  filters,
  setFilters,
}: {
  logs: ExceptionLog[];
  filters: ExceptionLogFilters;
  setFilters: React.Dispatch<React.SetStateAction<ExceptionLogFilters>>;
}) {
  const actionCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const log of logs) {
      counts[log.action] = (counts[log.action] ?? 0) + 1;
    }

    return counts;
  }, [logs]);

  const toggleAction = useCallback(
    (action: string) => {
      setFilters((prev) => {
        const current = prev.actions ?? [];
        if (current.includes(action)) {
          const next = current.filter((a) => a !== action);

          return { ...prev, actions: next.length > 0 ? next : undefined };
        }

        return { ...prev, actions: [...current, action] };
      });
    },
    [setFilters]
  );

  if (logs.length === 0) {
    return null;
  }

  const activeActions = filters.actions ?? [];

  return (
    <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-gray-200 bg-gray-50 text-sm">
      {EXCEPTION_ACTIONS.map((action) => {
        const count = actionCounts[action];
        if (!count) {
          return null;
        }

        const isActive = activeActions.includes(action);

        return (
          <button
            key={action}
            type="button"
            onClick={() => toggleAction(action)}
            className={`inline-flex items-center rounded-full px-2.5 py-2 select-none outline-none text-xs font-medium cursor-pointer transition-colors ${
              isActive ? 'bg-gray-500 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            {ACTION_LABELS[action] ?? action}
            <span className={`mx-1.5 h-3 w-px ${isActive ? 'bg-gray-300' : 'bg-gray-400'}`} />
            {count}
          </button>
        );
      })}
    </div>
  );
}
